import {
  withTypes,
  Field,
} from 'react-final-form';
import { Mutator } from 'final-form';
import { t } from 'ttag';

import { Title } from '@ui/Title';
import { Button } from '@ui/Button';
import { FormError } from '@ui/FormError';
import { InputLabel } from '@ui/InputLabel';
import { SelectOption } from '@ui/Select';
import { DeliveryPointSelectContainer } from '@containers/DeliveryPointSelect';
import { RegionSelect } from '@containers/RegionSelect';
import { DeliveryCitySelect } from '@containers/DeliveryCitySelect';
import { required } from '@utils/validators';
import { deliveryChangeMutator } from '@utils/mutators';
import { DeliveryService, WarehousePurpose } from '@newModels';

// styles
import s from './DeliverySettingForm.sss';

// types
type RegionSelectType = SelectOption<string> | undefined;
type CitySelectType = SelectOption<string> | undefined;
type WareHouseSelectType = SelectOption<string> | undefined;

export type FormData = {
  region?: RegionSelectType;
  city: CitySelectType;
  warehouse: WareHouseSelectType;
};

type Region = {
  id: string
  name: string
};
type City = Region;
type Warehouse = Region;

type DeliverySettingFormProps = {
  initialData: {
    region?: Region | null;
    city?: City | null;
    warehouse?: Warehouse | null;
  }
  deliveryService: DeliveryService;
  onCancel: () => void;
  onSubmit: (arg1: FormData) => void;
};

export const DeliverySettingForm: React.FC<DeliverySettingFormProps> = ({
  initialData,
  deliveryService,
  onSubmit,
  onCancel,
}) => {
  const { Form } = withTypes<FormData>();

  const promptRegion = (
    deliveryService
    && deliveryService === DeliveryService.Justin
  );

  const initialRegion = initialData.region
    ? {
      label: initialData.region.name,
      value: initialData.region.id,
    }
    : undefined;

  const initialCity = initialData.city
    ? {
      label: initialData.city.name,
      value: initialData.city.id,
    }
    : undefined;

  const initialWarehouse = initialData.warehouse
    ? {
      label: initialData.warehouse.name,
      value: initialData.warehouse.id,
    }
    : undefined;

  return (
    (
      <Form
        onSubmit={onSubmit}
        mutators={{
          deliveryChangeMutator: deliveryChangeMutator as Mutator<FormData>,
        }}
        render={
        ({ form, values, handleSubmit }) => (
          <form className={s.root} onSubmit={handleSubmit}>
            <Title type="h3" bold={false}>
              {t`Виберіть відділення щоб надіслати товар`}
            </Title>

            {
              promptRegion
              && (
              <>
                <InputLabel className={s.inputLabel}>
                  {t`Область`}
                </InputLabel>
                <Field<RegionSelectType>
                  initialValue={initialRegion}
                  name="region"
                  validate={(option) => required(option?.value)}
                >
                  {({ input, meta }) => (
                    <>
                      <RegionSelect
                        {...input}
                        onChange={
                          (value) => {
                            if (value !== values.region) {
                              form.mutators.deliveryChangeMutator({
                                changedField: 'region',
                              });
                            }
                            input.onChange(value);
                          }
                        }
                        className={s.input}
                        invalid={meta.error && meta.touched}
                      />
                      {meta.error && meta.touched
                      && <FormError>{meta.error}</FormError>}
                    </>
                  )}
                </Field>
              </>
              )
            }

            <InputLabel className={s.inputLabel}>
              {t`Місто`}
            </InputLabel>
            <Field<CitySelectType>
              name="city"
              initialValue={initialCity}
              validate={(option) => required(option?.value)}
            >
              {({ input, meta }) => (
                <>
                  <DeliveryCitySelect
                    {...input}
                    onChange={
                      (value) => {
                        if (value !== values.city) {
                          form.mutators.deliveryChangeMutator({
                            changedField: 'city',
                          });
                        }
                        input.onChange(value);
                      }
                    }
                    deliveryService={deliveryService}
                    regionId={values.region?.value}
                    className={s.input}
                    invalid={meta.error && meta.touched}
                    isDisabled={promptRegion && !values.region}
                  />
                  {meta.error && meta.touched
                    && <FormError>{meta.error}</FormError>}
                </>
              )}
            </Field>

            <InputLabel className={s.inputLabel}>
              {t`Відділення`}
            </InputLabel>
            <Field<WareHouseSelectType>
              name="warehouse"
              initialValue={initialWarehouse}
              validate={(option) => required(option?.value)}
            >
              {({ input, meta }) => (
                <>
                  <DeliveryPointSelectContainer
                    {...input}
                    deliveryService={deliveryService}
                    cityId={values.city?.value || ''}
                    className={s.input}
                    invalid={meta.error && meta.touched}
                    isDisabled={!values.city}
                    purpose={WarehousePurpose.Sending}
                  />
                  <div className={s.inputErrors}>
                    {meta.error && meta.touched && values.city
                      && <FormError>{meta.error}</FormError>}
                  </div>
                </>
              )}
            </Field>

            <div className={s.formButton}>
              <Button
                type="button"
                theme="secondary"
                onClick={onCancel}
                className={s.submitButton}
              >
                {t`Відміна`}
              </Button>
              <Button
                type="submit"
                className={s.submitButton}
              >
                {t`Зберегти`}
              </Button>
            </div>

          </form>
        )
      }
      />
    )
  );
};
